import { useRef, useState } from "preact/hooks";

import { WeatherDetailOverlay } from "./WeatherDetailOverlay";
import { gardenChipLabel } from "../lib/weatherCopy";
import type { GardenOut } from "../types";

export interface GardenChipProps {
  garden: GardenOut | null;
}

const TITLE_ID = "garden-chip-title";

/**
 * Sprint-015 C3 (weather-hub-ia-layout-2026-07-10.md §4). Renders nothing
 * when `garden === null` (`farm_settings.garden_exists === false`) — a
 * data-presence condition evaluated independently of the heat-status
 * component's own state (§4: a heat-quiet day can still be a garden-water
 * day, and a HIGH-hero day can still carry the chip simultaneously).
 * Deliberately the quietest element on the card — no severity tone, ever
 * (`garden.state` is a binary water/skip/unknown signal, not a tiered
 * one); the caller (`DashboardView`) is responsible for placing this
 * BELOW the heat-status pill/hero at every state, per the "livestock
 * outranks garden convenience" hierarchy. No raw ET/ET0 number ever
 * renders here (`lib/weatherCopy.ts::gardenChipLabel`). Dashboard-only —
 * never rendered on the Today Card (§4's own explicit exclusion; C5 is
 * out of this task's scope).
 */
export function GardenChip({ garden }: GardenChipProps) {
  const [detailOpen, setDetailOpen] = useState(false);
  const chipRef = useRef<HTMLButtonElement>(null);

  if (!garden) return null;

  return (
    <>
      <div class="garden-chip-wrap">
        <button
          ref={chipRef}
          type="button"
          class="garden-chip"
          aria-haspopup="dialog"
          onClick={() => setDetailOpen(true)}
        >
          <span class="garden-chip-icon" aria-hidden="true">
            🌱
          </span>
          {gardenChipLabel(garden.state)}
          <span class="garden-chip-chevron" aria-hidden="true">
            ›
          </span>
        </button>
      </div>
      <WeatherDetailOverlay
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        titleId={TITLE_ID}
        title="Garden watering"
        triggerRef={chipRef}
      >
        <p class="heat-sheet-sub">{garden.detail}</p>
      </WeatherDetailOverlay>
    </>
  );
}
